import { HTMLAttributes, useMemo } from 'react'

import { Divider, Heading } from '@hhgtech/hhg-components'
import { Box, MantineTheme, rem } from '@mantine/core'
import { PremiumContentBanner } from 'components/atoms/premiumContentBanner'
import { ReadMoreButton } from 'components/atoms/readMoreButton'
import { AdBlock } from 'components/molecules/adBlock'
import { RelatedArticleCarousel } from 'components/molecules/relatedArticleCarousel'
import { PromoteArticleMap } from 'components/pages/article/promoteArticles'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { sanitize } from 'isomorphic-dompurify'
import { useIntl } from 'react-intl'

import { FormattedPostItemContent } from './articleContents'
import {
  Affiliate,
  BabyGrowthCard,
  BabyPoopCard,
  BabyVaccinationEmbed,
  // BMICard,
  // BMRCard,
  DueDateCardEmbeded,
  EmbeddedCommunity,
  InjectedRelatedPosts,
  OvulationCardEmbeded,
  PWGEmbedded,
} from './dynamicContentComponents'
import {
  StyledArticleContentWrapper,
  StyledEmbedHealtool,
  StyledEmbedSpotlight,
  StyledVideoList,
} from './index.styled'

export type Props = {
  content: FormattedPostItemContent
  isShowAll?: boolean
  isShowAllPremium?: boolean
  isHidden?: boolean
  onReadMoreClick?: () => void
  articleFontSize?: string | null
  articleId: string
  openAskDoctor?: (e: boolean) => void
  adUnitPath?: string
  articleLink?: string
  customTargetings?: { [key: string]: (string | number) | (string | number)[] }
} & HTMLAttributes<HTMLDivElement>
export const ArticleContentItem = ({
  content,
  isShowAll,
  isShowAllPremium,
  isHidden: isHiddenProps,
  onReadMoreClick,
  className,
  articleFontSize,
  articleId,
  openAskDoctor,
  adUnitPath,
  articleLink,
  customTargetings,
  ...rest
}: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { locale } = useRouterExtend()

  const {
    html,
    injectedSlug,
    adUnitItem,
    injectedHealthTool,
    injectedSpotlight,
    injectedCommunity,
    injectAffiliate,
    isPremium,
    isReadMore,
    injectVideo,
    is25Percent,
    relatedArticles,
    // is59Percent,
    is5Percent,
    // is50Percent,
    // is80Percent,
    is15Percent,
  } = content
  const isPremiumHidden = !isShowAllPremium && !isPremium
  // const isReadMoreHidden = !isShowAll && !isReadMore
  const isHidden = isHiddenProps || isPremiumHidden
  const hasPromotionArticle =
    locale === 'vi-VN' && !!PromoteArticleMap[articleLink || '']

  const sanitizedHtml = useMemo(
    () =>
      html &&
      sanitize(html, {
        ADD_ATTR: ['name', 'target', 'data-url'],
        ADD_TAGS: ['iframe'],
      }),
    [html],
  )

  const healthToolWrapperStyles = (theme: MantineTheme) => ({
    root: {
      [theme.fn.smallerThan('sm')]: {
        borderRadius: 0,
        borderLeft: 0,
        borderRight: 0,
        marginLeft: rem(-16),
        marginRight: rem(-16),
      },
    },
  })

  return (
    <div
      className={`unique-content ${className || ''}`}
      data-hidden={isHidden}
      style={
        isHidden
          ? {
              height: 0,
              opacity: 0,
              transition: 'opacity 0.5s ease-in-out',
              pointerEvents: 'none',
              overflow: 'hidden',
            }
          : {
              opacity: 1,
              transition: 'opacity 0.5s ease-in-out',
            }
      }
      {...rest}
    >
      {isPremium && !isShowAllPremium && (
        <PremiumContentBanner isPortal={false} />
      )}
      {isReadMore && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            // marginBottom: isShowAll ? '-1em' : 0,
          }}
        >
          <div
            style={
              isShowAll
                ? {
                    transform: 'translateY(100vh)',
                    opacity: 0,
                    pointerEvents: 'none',
                    position: 'absolute',
                    width: '100%',
                    transition: 'all 0.5s ease-in-out',
                  }
                : {
                    transition: 'all 0.5s ease-in-out',
                  }
            }
          >
            <ReadMoreButton onClick={onReadMoreClick} />
          </div>
        </div>
      )}
      {sanitizedHtml && (
        <StyledArticleContentWrapper
          className="body-content article-content-wrapper"
          data-size={articleFontSize || 'small'}
          dangerouslySetInnerHTML={{
            __html: sanitizedHtml,
          }}
        />
      )}
      {!hasPromotionArticle && (
        <>
          {injectedSlug && (
            <InjectedRelatedPosts
              slug={injectedSlug}
              articleType={1}
              isMobile={isMobile}
            />
          )}

          {relatedArticles && (
            <RelatedArticleCarousel
              title={relatedArticles.title}
              ids={relatedArticles.ids}
            />
          )}
          {injectedHealthTool && (
            <StyledEmbedHealtool>
              {/* {injectedHealthTool === 'bmi' && (
                <BMICard styles={healthToolWrapperStyles} />
              )} */}
              {/* {injectedHealthTool === 'bmr' && (
                <BMRCard styles={healthToolWrapperStyles} />
              )} */}
              {(injectedHealthTool === 'due-date-calculator' ||
                injectedHealthTool === 'due-date') && (
                <DueDateCardEmbeded styles={healthToolWrapperStyles} />
              )}
              {(injectedHealthTool === 'ovulation' ||
                injectedHealthTool === 'ovulation-calculator') && (
                <OvulationCardEmbeded styles={healthToolWrapperStyles} />
              )}
              {injectedHealthTool === 'child-growth-chart' && (
                <BabyGrowthCard styles={healthToolWrapperStyles} />
              )}
              {injectedHealthTool === 'preweight-gain-calculator' && (
                <PWGEmbedded styles={healthToolWrapperStyles} />
              )}
              {injectedHealthTool === 'baby-poop-checking' && (
                <BabyPoopCard styles={healthToolWrapperStyles} />
              )}
              {injectedHealthTool === 'vaccination-tool' && (
                <BabyVaccinationEmbed styles={healthToolWrapperStyles} />
              )}
            </StyledEmbedHealtool>
          )}
          {typeof injectedCommunity !== 'undefined' &&
            injectedCommunity?.id && (
              <EmbeddedCommunity
                titleMember={injectedCommunity?.titleMember || ''}
                titleGuest={injectedCommunity?.titleGuest || ''}
                descriptionMember={injectedCommunity?.descriptionMember || ''}
                descriptionGuest={injectedCommunity?.descriptionGuest || ''}
                communityId={injectedCommunity?.id}
                articleId={articleId}
                openAskDoctor={openAskDoctor}
              />
            )}
          {injectedSpotlight ? (
            <>
              <Heading tag="h3" style={{ marginBottom: 16 }}>
                {f({
                  id: 'home.recommendSpotlight',
                  defaultMessage: 'Spotlight',
                })}
              </Heading>
              <StyledEmbedSpotlight>
                <a
                  href={injectedSpotlight?.permalink || '#'}
                  data-event-category="Spotlight"
                  data-event-action="Click 1"
                  data-event-label={`https://${domainLocales[locale]}${injectedSpotlight?.permalink}`}
                >
                  <img
                    loading="lazy"
                    src={
                      (isMobile
                        ? injectedSpotlight?.image
                        : injectedSpotlight?.desktopBanner) ||
                      injectedSpotlight?.image
                    }
                  />
                </a>
              </StyledEmbedSpotlight>
              <Divider type="solid" />
            </>
          ) : null}

          {injectAffiliate && injectAffiliate?.productId ? (
            <Affiliate
              isMobile={!!isMobile}
              productId={String(injectAffiliate.productId)}
              openNewTab={injectAffiliate?.openNewTab}
              noFollow={injectAffiliate?.noFollow}
              sponsored={injectAffiliate?.sponsored}
              isFocus={false}
              isCarousel={false}
              isCarouselCollapse={true}
              changeCollapseCarousel={() => {
                //
              }}
            />
          ) : null}
        </>
      )}

      {injectVideo && (
        <StyledVideoList
          title={<Heading tag="h5">{injectVideo.title}</Heading>}
          description={injectVideo.description}
          videos={injectVideo.url?.map((u) => ({
            title: '',
            videoUrl: u.trim(),
          }))}
        />
      )}

      {adUnitPath && ((is25Percent && isMobile) || (is5Percent && !isMobile)) && (
        <Box sx={{ margin: '5px 0' }}>
          <AdBlock
            className="article-ad"
            divId={`div-gpt-ad-afterimage-${articleId}`}
            adUnitPath={adUnitPath}
            size={[1, 1]}
            pos={['AfterImage']}
            contentId={articleId}
            customTargetings={customTargetings}
          />
        </Box>
      )}

      {adUnitPath && ((is15Percent && isMobile) || (is25Percent && !isMobile)) && (
        <Box sx={{ margin: '5px 0' }}>
          <AdBlock
            key={'div-gpt-ad-video-' + articleId + '-oop'}
            adUnitPath={adUnitPath}
            size={[1, 1]}
            pos={['video']}
            contentId={articleId}
            divId={'div-gpt-ad-video-' + articleId + '-oop'}
            className="sh-player"
            customTargetings={customTargetings}
          />
        </Box>
      )}

      {adUnitItem && adUnitPath && (
        <div
          className="article-ad-container"
          style={{
            marginTop: injectedSlug ? '1rem' : '',
          }}
        >
          <AdBlock
            className="article-ad"
            adUnitPath={adUnitPath}
            size={adUnitItem.size}
            divId={adUnitItem.divId}
            pos={adUnitItem.pos}
            contentId={articleId}
            isHidden={!isMobile}
            onlyRefreshInview
            refreshInterval={30}
            customTargetings={customTargetings}
          />
        </div>
      )}
    </div>
  )
}
